import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  Menu, 
  X, 
  ChevronDown, 
  Upload,
  Globe2,
  Search,
  Brain,
  Link2,
  Layers,
  Workflow,
  HelpCircle,
  Users,
  BarChart,
  CreditCard,
  Building2,
  Info,
  BookOpen,
  Mail,
  BookText,
  AlertCircle,
  AlignLeft,
  MessageSquare,
  Lock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import LoginModal from '@/components/LoginModal';
import useAnalytics from '../hooks/useAnalytics';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { handleNavigationScroll, handleTabScroll } from '@/utils/scrollUtils';

// Add error boundary
class NavbarErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Navbar Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <header className="fixed top-0 left-0 w-full bg-white shadow-sm z-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4">
            <Link to="/" className="flex items-center">
              {/* Fallback logo */}
              <span className="text-xl font-bold">Strive</span>
            </Link>
          </div>
        </header>
      );
    }

    return this.props.children;
  }
}

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const analytics = useAnalytics();
  const navigate = useNavigate();

  // Make navigate available globally for scroll utilities
  useEffect(() => {
    (window as any).__navigate = navigate;
    return () => {
      delete (window as any).__navigate;
    };
  }, [navigate]);

  // Add error handling for scroll events
  useEffect(() => {
    const handleScroll = () => {
      try {
        if (window.scrollY > 20) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      } catch (error) {
        console.error('Scroll handler error:', error);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add error handling for click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      try {
        if (openMenu && !(event.target as Element).closest('.mega-menu-container')) {
          setOpenMenu(null);
        }
      } catch (error) {
        console.error('Click outside handler error:', error);
        setOpenMenu(null); // Safely close menu on error
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenu]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMenu = (menu: string) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  // Add error handling for login
  const handleLoginClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      analytics.trackEvent('login_attempt', {
        source: 'navbar',
        location: 'header',
        timestamp: new Date().toISOString()
      });
      
      setIsLoginModalOpen(true);
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    } catch (error) {
      console.error('Login handler error:', error);
      // Fallback to opening modal even if analytics fails
      setIsLoginModalOpen(true);
    }
  };

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(e.target as HTMLElement).closest('button')) {
      return;
    }
    analytics.trackEvent('demo_request', {
      source: 'navbar',
      location: 'header',
      timestamp: new Date().toISOString()
    });
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Add keyboard navigation support
  const handleKeyDown = (e: React.KeyboardEvent, menu: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu(menu);
    } else if (e.key === 'Escape') {
      setOpenMenu(null);
    }
  };

  // Add handleSectionClick function
  const handleSectionClick = useCallback((e: React.MouseEvent, path: string, sectionId: string, action?: string) => {
    e.preventDefault();
    
    // If we're already on the correct path, just scroll
    if (window.location.pathname === path) {
      // If this is the workflow section and we have an action, trigger tab change first
      if (sectionId === 'workflow-jobs' && action) {
        const tabId = action === 'templates' ? 'templates-tab' : 'custom-tab';
        handleTabScroll(tabId, sectionId);
      } else {
        handleNavigationScroll(path, sectionId);
      }
      return;
    }

    // Otherwise navigate and then scroll
    navigate(path);
    
    // Use a longer timeout and check for section existence
    const scrollToSection = () => {
      const section = document.getElementById(sectionId);
      if (section) {
        // If this is the workflow section and we have an action, trigger tab change first
        if (sectionId === 'workflow-jobs' && action) {
          const tabId = action === 'templates' ? 'templates-tab' : 'custom-tab';
          handleTabScroll(tabId, sectionId);
        } else {
          handleNavigationScroll(path, sectionId);
        }
      } else {
        // If section not found yet, try again in 100ms
        setTimeout(scrollToSection, 100);
      }
    };

    // Initial delay to ensure navigation completes
    setTimeout(scrollToSection, 300);
  }, [navigate]);

  return (
    <NavbarErrorBoundary>
      <header
        className={cn(
            'fixed top-0 left-0 w-full z-[9999] transition-all duration-300 fixed-navbar',
            scrolled || openMenu 
              ? 'bg-white shadow-sm' 
              : 'bg-transparent'
        )}
        role="banner"
      >
          <div className="relative">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4">
              <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center" aria-label="Strive Home" onClick={(e) => {
          if (window.location.pathname === '/') {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            e.preventDefault();
            navigate('/');
            // Scroll to top after navigation
            window.scrollTo({ top: 0 });
          }
        }}>
          <svg width="120" height="32" viewBox="0 0 242 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M212.986 44.9261C207.647 44.9261 202.307 44.9261 196.973 44.9261C195.177 44.9261 195.038 45.1788 195.909 46.7596C199.812 53.8573 209.566 55.2392 215.244 49.475C216.035 48.6739 216.847 48.3082 217.981 48.3244C221.336 48.3835 224.692 48.3351 228.041 48.3566C229.466 48.3674 229.638 48.647 229.015 49.959C225.504 57.3254 219.686 61.7345 211.744 63.4337C201.716 65.5791 191.698 61.3635 186.327 53.5992C179.17 43.2377 181.665 29.6716 192.048 22.1546C203.662 13.745 220.438 17.0518 227.853 29.2253C230.531 33.6183 231.391 38.4146 230.945 43.5012C230.843 44.6895 230.203 44.953 229.144 44.9476C223.756 44.9207 218.374 44.9369 212.986 44.9369V44.9261ZM206.636 36.6455C210.029 36.6455 213.427 36.6402 216.82 36.6455C217.868 36.6455 218.245 36.2799 217.696 35.2744C215.33 30.9406 211.69 28.5155 206.754 28.4833C201.818 28.4456 198.108 30.7685 195.747 35.1292C195.054 36.409 195.226 36.6348 196.731 36.6402C200.033 36.6509 203.334 36.6402 206.636 36.6402V36.6455Z" fill="#18174D"/>
            <path d="M16.5278 49.2112C18.1355 49.2112 19.7432 49.2381 21.3455 49.2004C22.1843 49.1789 22.6468 49.437 22.7919 50.3403C23.1038 52.2814 24.4911 53.2869 26.244 53.6472C28.099 54.029 29.9864 53.9752 31.7823 53.1095C34.6697 51.7115 35.116 48.4208 32.3307 46.8776C30.2498 45.7269 27.7603 45.2322 25.3998 44.6676C21.0928 43.646 17.1515 41.9576 14.0006 38.7691C9.02687 33.7308 10.8497 26.7569 14.4415 23.0629C19.2593 18.1053 25.3084 16.8901 31.9059 17.8257C35.9333 18.3957 39.5305 19.998 42.2781 23.1167C44.2246 25.332 45.4452 27.8861 45.6495 30.8864C45.7248 31.9564 45.3376 32.3167 44.2945 32.3059C41.1758 32.2683 38.0572 32.3221 34.9386 32.2575C34.5084 32.2468 33.8255 31.8812 33.6965 31.5209C32.5028 28.1818 29.7713 26.7892 26.3354 27.8699C26.0181 27.9667 25.7063 28.0796 25.3998 28.1979C24.1416 28.6711 23.3995 29.5691 23.1898 30.9025C22.9801 32.2199 23.5555 33.1931 24.5771 33.9459C26.8892 35.6504 29.5938 36.3064 32.3576 36.7473C35.9387 37.3173 39.3531 38.226 42.2405 40.6026C47.9132 45.2698 47.069 53.8999 42.6169 58.4273C38.8207 62.2826 34.1051 63.8688 28.8034 63.9441C23.2006 64.0248 18.1946 62.5031 14.1565 58.3951C11.9681 56.169 10.9626 53.4375 10.527 50.4264C10.3872 49.4746 10.7905 49.1682 11.71 49.195C13.3177 49.2434 14.9254 49.2058 16.5278 49.2058V49.2112Z" fill="#18174D"/>
            <path d="M52.3652 24.3802C52.3652 18.1913 52.4028 11.997 52.3383 5.80807C52.3222 4.43156 52.7631 4.00678 54.1181 4.0498C57.0002 4.1412 59.8822 4.13045 62.7643 4.0498C64.0386 4.01753 64.3881 4.50146 64.372 5.70591C64.3182 9.53432 64.3881 13.3627 64.3344 17.1911C64.3182 18.3687 64.7484 18.7827 65.8991 18.772C69.0177 18.729 72.1364 18.7827 75.255 18.7505C76.239 18.7397 76.7337 18.9871 76.7068 20.0947C76.6477 22.6434 76.653 25.1975 76.7068 27.7515C76.7283 28.8431 76.2659 29.1227 75.2658 29.1065C72.1955 29.0635 69.1199 29.128 66.0496 29.0743C64.7753 29.0528 64.286 29.4991 64.329 30.811C64.4527 34.5857 64.2107 38.3926 64.6086 42.135C65.2431 48.0604 69.1091 51.9211 74.9539 52.8836C76.2928 53.104 76.7875 53.518 76.7176 54.9268C76.5939 57.4701 76.6477 60.0296 76.7014 62.5783C76.7229 63.5945 76.438 63.9763 75.3518 63.9978C62.7213 64.1967 52.6448 54.5934 52.4082 41.9629C52.3007 36.1074 52.3867 30.2465 52.3867 24.3855H52.3544L52.3652 24.3802Z" fill="#18174D"/>
            <path d="M84.0783 49.362C84.0783 45.7702 83.917 42.173 84.1106 38.5973C84.6429 28.5262 91.961 19.5036 102.559 17.9228C104.048 17.7023 105.559 17.627 107.06 17.498C107.866 17.4281 108.285 17.7292 108.275 18.6164C108.248 21.4985 108.242 24.3805 108.275 27.2626C108.285 28.4025 107.538 28.51 106.694 28.6283C102.048 29.279 98.7521 31.8061 97.0207 36.1185C96.354 37.78 96.0744 39.6888 96.0421 41.4901C95.9292 48.0554 95.9668 54.6261 96.026 61.1913C96.0367 62.4549 95.6657 62.8098 94.4236 62.7722C91.4985 62.6861 88.5627 62.6754 85.6376 62.7722C84.2181 62.8206 83.8847 62.299 83.9116 60.9816C83.9869 57.1102 83.9385 53.2334 83.9385 49.3566H84.0837L84.0783 49.362Z" fill="#18174D"/>
            <path d="M126.062 40.871C126.062 47.5815 126.014 54.2866 126.1 60.997C126.121 62.4865 125.648 62.8252 124.255 62.7768C121.282 62.6746 118.303 62.7177 115.33 62.7607C114.362 62.7768 113.996 62.4596 113.996 61.4594C114.018 47.7105 114.018 33.9669 113.996 20.218C113.996 19.1103 114.416 18.7286 115.512 18.7447C118.534 18.7931 121.562 18.8092 124.583 18.7393C125.809 18.7124 126.084 19.2071 126.078 20.3255C126.041 27.1758 126.062 34.0261 126.062 40.8764V40.871Z" fill="#18174D"/>
            <path d="M141.473 23.1429L146.011 30.0846C148.21 28.8533 150.614 27.9553 153.162 27.4768C159.141 26.353 164.997 27.4337 169.885 30.2083L174.848 22.6267L174.869 22.5944C171.24 20.4544 167.185 18.9865 162.895 18.3305C161.26 18.0832 159.588 17.9434 157.894 17.9434C156.448 17.9434 155.028 18.0401 153.63 18.2122C150.726 18.5778 147.909 19.3091 145.177 20.4275C143.742 21.019 142.36 21.7072 141.037 22.4762L141.473 23.1429Z" fill="#FF6B35"/>
            <path d="M152.942 27.3466C150.394 27.8251 147.99 28.7231 145.791 29.9544L155.56 47.8756C157.56 47.8756 155.549 47.9025 157.56 47.8756C160.56 47.8756 157.56 47.8756 160.56 47.8756L169.665 30.0781C164.777 27.3036 158.922 26.2228 152.942 27.3466Z" fill="#FF6B35"/>
            <path d="M132.639 19.7613C133.585 19.7613 132.639 19.756 143.203 19.7289C145.78 19.7613 145.642 19.7289 146.581 21.9498C149.958 29.9346 153.306 37.0538 156.78 45.001C157.738 47.2324 156.78 45.001 157.738 47.2324C157.738 47.2324 157.738 47.2324 158.78 45.001C162.404 36.7581 165.793 29.4937 169.374 21.2346C169.78 19.7291 170.369 19.6968 171.579 19.7291C174.837 19.8151 178.096 19.756 181.36 19.7613C181.817 19.7613 182.274 19.8205 182.962 19.8635C182.704 20.541 182.548 21.0626 182.322 21.5465C175.961 35.1718 169.579 48.7809 163.261 62.4223C162.777 63.4709 162.196 63.7935 161.115 63.7612C158.803 63.6967 156.486 63.6967 154.173 63.7612C153.265 63.7881 152.797 63.4171 152.437 62.6482C146.027 48.8885 139.607 35.1395 133.193 21.3798C132.999 20.9658 132.897 20.5141 132.639 19.756V19.7613Z" fill="#18174D"/>
            <path d="M157.55 1.00011C161.319 1.02162 164.368 4.05424 164.4 7.82888C164.438 11.6519 161.357 14.7114 157.48 14.706C153.56 14.706 150.56 11.7164 150.57 7.83426C150.581 4.0166 153.684 0.978606 157.544 1.00011H157.55Z" fill="#18174D"/>
          </svg>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8" role="navigation" aria-label="Main navigation">
          <div className="flex items-center space-x-8" role="menubar">
            {/* Knowledge Intelligence */}
            <div className="relative mega-menu-container">
              <button
                onClick={() => toggleMenu('knowledge')}
                onKeyDown={(e) => handleKeyDown(e, 'knowledge')}
                className={cn(
                  "flex items-center space-x-1 text-sm font-medium transition-colors",
                  openMenu === 'knowledge' 
                    ? 'text-blue-700'
                    : 'text-gray-700 hover:text-blue-700'
                )}
                aria-expanded={openMenu === 'knowledge'}
                aria-controls="knowledge-menu"
                aria-haspopup="menu"
                role="menuitem"
              >
                <span>Knowledge Intelligence</span>
                <ChevronDown size={16} className={cn(
                  "transition-transform duration-200",
                  openMenu === 'knowledge' ? "rotate-180" : ""
                )} aria-hidden="true" />
              </button>

              {openMenu === 'knowledge' && (
                <div 
                  id="knowledge-menu"
                  className="fixed left-0 right-0 top-[72px] w-full bg-white shadow-lg border-t border-gray-100 z-[9991]"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 h-[320px]">
                    <div className="flex">
                      <div className="w-[65%]">
                        <ul className="grid grid-cols-2 gap-6" role="menu">
                          <li role="none">
                            <Link 
                              to="/knowledge-intelligence#document-uploads"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('knowledge');
                                handleSectionClick(e, '/knowledge-intelligence', 'document-uploads');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors mr-3">
                                <Upload className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">Document Uploads</h3>
                                <p className="text-sm text-gray-500">Upload and analyze any document type</p>
                              </div>
                            </Link>
                          </li>
                          <li role="none">
                            <Link 
                              to="/knowledge-intelligence#web-insights"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('knowledge');
                                handleSectionClick(e, '/knowledge-intelligence', 'web-insights');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors mr-3">
                                <Globe2 className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">Web Insights</h3>
                                <p className="text-sm text-gray-500">Monitor specific websites for updates</p>
                              </div>
                            </Link>
                          </li>
                          <li role="none">
                            <Link 
                              to="/knowledge-intelligence#web-crawler"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('knowledge');
                                handleSectionClick(e, '/knowledge-intelligence', 'web-crawler');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-3">
                                <Search className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-green-600">Web Crawler</h3>
                                <p className="text-sm text-gray-500">Automatic web search and monitoring</p>
                              </div>
                            </Link>
                          </li>
                          <li role="none">
                            <Link 
                              to="/knowledge-intelligence#knowledge-notes"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('knowledge');
                                handleSectionClick(e, '/knowledge-intelligence', 'knowledge-notes');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-yellow-50 text-yellow-600 group-hover:bg-yellow-100 transition-colors mr-3">
                                <AlignLeft className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-yellow-600">Contextual Training</h3>
                                <p className="text-sm text-gray-500">Teach AI about your business</p>
                              </div>
                            </Link>
                          </li>
                        </ul>
                      </div>
                      <div className="w-px bg-gray-200 mx-8"></div>
                      <div className="w-[35%] pl-4">
                        <div className="flex justify-between">
                          <div className="w-1/2">
                            <h3 className="font-medium text-gray-600 uppercase text-sm mb-4">Solutions</h3>
                            <div role="menu" className="space-y-3">
                              <div role="none">
                                <Link 
                                  to="/#product-management"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    handleSectionClick(e, '/', 'product-management');
                                  }}
                                >
                                  <Users size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Product Teams</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/#revenue-intelligence"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    handleSectionClick(e, '/', 'revenue-intelligence');
                                  }}
                                >
                                  <BarChart size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Revenue Teams</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/pricing"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                  }}
                                >
                                  <Building2 size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Pricing</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/faq"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    // If already on the FAQ page, just scroll to top
                                    if (window.location.pathname === '/faq') {
                                      e.preventDefault();
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                  }}
                                >
                                  <HelpCircle size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>FAQ</span>
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="w-1/2 pl-4">
                            <h3 className="font-medium text-gray-600 uppercase text-sm mb-4">Company</h3>
                            <div role="menu" className="space-y-3">
                              <div role="none">
                                <Link 
                                  to="/about"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <Info size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>About</span>
                                </Link>
                              </div>
                              <div role="none">
                                <a 
                                  href="https://matters-of-context.strive.us/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <BookOpen size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Blog</span>
                                </a>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/contact"
                                  state={{ tab: 'schedule' }}
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <Mail size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Contact</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link
                                  to="/glossary"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <BookText size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Glossary</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/contact"
                                  state={{ tab: 'newsletter' }}
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <MessageSquare size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Newsletter Signup</span>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* AI Section */}
            <div className="relative mega-menu-container">
              <button
                onClick={() => toggleMenu('ai')}
                onKeyDown={(e) => handleKeyDown(e, 'ai')}
                className={cn(
                  "flex items-center space-x-1 text-sm font-medium transition-colors",
                  openMenu === 'ai' 
                    ? 'text-blue-700'
                    : 'text-gray-700 hover:text-blue-700'
                )}
                aria-expanded={openMenu === 'ai'}
                aria-controls="ai-menu"
                aria-haspopup="menu"
                role="menuitem"
              >
                <span>Artificial Intelligence</span>
                <ChevronDown size={16} className={cn(
                  "transition-transform duration-200",
                  openMenu === 'ai' ? "rotate-180" : ""
                )} aria-hidden="true" />
              </button>

              {openMenu === 'ai' && (
                <div 
                  id="ai-menu"
                  className="fixed left-0 right-0 top-[72px] w-full bg-white shadow-lg border-t border-gray-100 z-[9991]"
                  role="menu"
                  aria-orientation="vertical"
                >
                  <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 h-[320px]">
                    <div className="flex">
                      <div className="w-[65%]">
                        <div className="grid grid-cols-2 gap-6">
                          <div role="menuitem">
                            <Link 
                              to="/#how-it-works"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('ai');
                                handleSectionClick(e, '/', 'how-it-works');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors mr-3">
                                <Brain className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-indigo-600">How It Works</h3>
                                <p className="text-sm text-gray-500">Learn about our AI technology</p>
                              </div>
                            </Link>
                          </div>
                          <div role="menuitem">
                            <Link 
                              to="/#workflow-jobs"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('ai');
                                handleSectionClick(e, '/', 'workflow-jobs', 'templates');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors mr-3">
                                <Layers className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">Workflow Templates</h3>
                                <p className="text-sm text-gray-500">Pre-built AI workflows for common tasks</p>
                              </div>
                            </Link>
                          </div>
                          <div role="menuitem">
                            <Link 
                              to="/#workflow-jobs"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('ai');
                                handleSectionClick(e, '/', 'workflow-jobs', 'custom');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors mr-3">
                                <Workflow className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">Custom Workflows</h3>
                                <p className="text-sm text-gray-500">Build your own automation</p>
                              </div>
                            </Link>
                          </div>
                          <div role="menuitem">
                            <Link 
                              to="/#chat-with-data"
                              className="flex items-start space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu('ai');
                                handleSectionClick(e, '/', 'chat-with-data');
                              }}
                            >
                              <div className="mt-1 p-2 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-3">
                                <MessageSquare className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-medium text-gray-900 group-hover:text-green-600">Chat with Data</h3>
                                <p className="text-sm text-gray-500">Interactive conversations with your data</p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="w-px bg-gray-200 mx-8"></div>
                      <div className="w-[35%] pl-4">
                        <div className="flex justify-between">
                          <div className="w-1/2">
                            <h3 className="font-medium text-gray-600 uppercase text-sm mb-4">Solutions</h3>
                            <div role="menu" className="space-y-3">
                              <div role="none">
                                <Link 
                                  to="/#product-management"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    handleSectionClick(e, '/', 'product-management');
                                  }}
                                >
                                  <Users size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Product Teams</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/#revenue-intelligence"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    handleSectionClick(e, '/', 'revenue-intelligence');
                                  }}
                                >
                                  <BarChart size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Revenue Teams</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/pricing"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                  }}
                                >
                                  <Building2 size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Pricing</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/faq"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenu(null);
                                    // If already on the FAQ page, just scroll to top
                                    if (window.location.pathname === '/faq') {
                                      e.preventDefault();
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }
                                  }}
                                >
                                  <HelpCircle size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>FAQ</span>
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="w-1/2 pl-4">
                            <h3 className="font-medium text-gray-600 uppercase text-sm mb-4">Company</h3>
                            <div role="menu" className="space-y-3">
                              <div role="none">
                                <Link 
                                  to="/about"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <Info size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>About</span>
                                </Link>
                              </div>
                              <div role="none">
                                <a 
                                  href="https://matters-of-context.strive.us/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <BookOpen size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Blog</span>
                                </a>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/contact"
                                  state={{ tab: 'schedule' }}
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <Mail size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Contact</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link
                                  to="/glossary"
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <BookText size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Glossary</span>
                                </Link>
                              </div>
                              <div role="none">
                                <Link 
                                  to="/contact"
                                  state={{ tab: 'newsletter' }}
                                  className="flex items-center space-x-2 text-gray-700 hover:text-primary text-sm group"
                                  role="menuitem"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <MessageSquare size={16} className="text-gray-600 group-hover:text-primary" />
                                  <span>Newsletter Signup</span>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Link */}
            <div role="none">
              <Link 
                to="/pricing"
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors"
                role="menuitem"
              >
                <span>Pricing</span>
              </Link>
            </div>

            {/* Integrations Link */}
            <div role="none">
              <Link
                to="/knowledge-intelligence#system-integrations"
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSectionClick(e, '/knowledge-intelligence', 'system-integrations');
                  setOpenMenu(null);
                }}
              >
                <span>Integrations</span>
              </Link>
            </div>

            {/* Trust Center Link */}
            <div role="none">
              <a
                href="https://trust.strive.us"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-blue-700 transition-colors"
                role="menuitem"
              >
                <span>Trust Center</span>
              </a>
            </div>
          </div>
        </nav>
        
        <div className="hidden md:flex space-x-4 items-center">
          <Button 
            variant="outline"
            className="text-sm font-medium px-4 py-2 rounded-lg text-gray-700 hover:text-blue-700 hover:bg-blue-50 hover:shadow-sm transition-all duration-300 ease-in-out"
            onClick={handleLoginClick}
            aria-label="Log in to your account"
          >
            <span>Log in</span>
          </Button>
          <Button 
            onClick={handleDemoClick}
            className="text-sm font-medium px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 hover:shadow-md hover:translate-y-[-2px] transition-all duration-300 ease-in-out border-0 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label="Request a demo"
          >
            <span>Book a demo</span>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-gray-700 focus:outline-none p-2 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={toggleMobileMenu}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-haspopup="true"
        >
          {mobileMenuOpen ? 
            <X size={24} aria-hidden="true" /> : 
            <Menu size={24} aria-hidden="true" />
          }
        </button>
            </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-gray-800/30 backdrop-blur-sm z-40 mobile-menu-backdrop"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            id="mobile-menu"
            className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white shadow-xl z-50 flex flex-col overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex-1 p-6 space-y-8">
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900">Knowledge Intelligence</h3>
                <ul className="grid gap-2" role="list">
                  <li>
                    <Link 
                      to="/knowledge-intelligence#document-uploads"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/knowledge-intelligence', 'document-uploads');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors mr-3">
                        <Upload className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Document Uploads</p>
                        <p className="text-sm text-gray-500">Upload and analyze any document type</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/knowledge-intelligence#web-insights"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/knowledge-intelligence', 'web-insights');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors mr-3">
                        <Globe2 className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Web Insights</p>
                        <p className="text-sm text-gray-500">Monitor specific websites for updates</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/knowledge-intelligence#web-crawler"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/knowledge-intelligence', 'web-crawler');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-3">
                        <Search className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Web Crawler</p>
                        <p className="text-sm text-gray-500">Automatic web search and monitoring</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/knowledge-intelligence#knowledge-notes"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/knowledge-intelligence', 'knowledge-notes');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-yellow-50 text-yellow-600 group-hover:bg-yellow-100 transition-colors mr-3">
                        <AlignLeft className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Contextual Training</p>
                        <p className="text-sm text-gray-500">Teach AI about your business</p>
                      </div>
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900">Artificial Intelligence</h3>
                <ul className="grid gap-2" role="list">
                  <li>
                    <Link 
                      to="/#how-it-works"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/', 'how-it-works');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors mr-3">
                        <Brain className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">How It Works</p>
                        <p className="text-sm text-gray-500">Learn about our AI technology</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/#workflow-jobs"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/', 'workflow-jobs', 'templates');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors mr-3">
                        <Layers className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Workflow Templates</p>
                        <p className="text-sm text-gray-500">Pre-built AI workflows for common tasks</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/#workflow-jobs"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/', 'workflow-jobs', 'custom');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors mr-3">
                        <Workflow className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Custom Workflows</p>
                        <p className="text-sm text-gray-500">Build your own automation</p>
                      </div>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/#chat-with-data"
                      className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                        handleSectionClick(e, '/', 'chat-with-data');
                      }}
                      role="menuitem"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-3">
                        <MessageSquare className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Chat with Data</p>
                        <p className="text-sm text-gray-500">Interactive conversations with your data</p>
                      </div>
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900">Company</h3>
                <div className="grid gap-2" role="list">
                  <Link 
                    to="/about" 
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors mr-3">
                      <Info className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">About</p>
                      <p className="text-sm text-gray-500">Learn about our company</p>
                    </div>
                  </Link>

                  <a 
                    href="https://matters-of-context.strive.us/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-3">
                      <BookOpen className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Blog</p>
                      <p className="text-sm text-gray-500">Latest insights and updates</p>
                    </div>
                  </a>

                  <Link 
                    to="/contact" 
                    state={{ tab: 'schedule' }}
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-orange-50 text-orange-600 group-hover:bg-orange-100 transition-colors mr-3">
                      <Mail className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Contact</p>
                      <p className="text-sm text-gray-500">Get in touch with us</p>
                    </div>
                  </Link>

                  <Link 
                    to="/glossary" 
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-pink-50 text-pink-600 group-hover:bg-pink-100 transition-colors mr-3">
                      <BookText className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Glossary</p>
                      <p className="text-sm text-gray-500">Key terms and definitions</p>
                    </div>
                  </Link>

                  <Link 
                    to="/contact" 
                    state={{ tab: 'newsletter' }}
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors mr-3">
                      <MessageSquare className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Newsletter Signup</p>
                      <p className="text-sm text-gray-500">Subscribe for updates</p>
                    </div>
                  </Link>

                  <a 
                    href="https://trust.strive.us" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    role="listitem"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors mr-3">
                      <Lock className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Trust Center</p>
                      <p className="text-sm text-gray-500">Security and compliance</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="p-6 border-t border-gray-200">
              <div className="grid gap-4" role="group" aria-label="Account actions">
                <Button 
                  variant="outline"
                  className="w-full justify-center text-base"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleLoginClick(e);
                  }}
                  aria-label="Log in to your account"
                >
                  <span>Log in</span>
                </Button>
                <Button 
                  onClick={handleDemoClick}
                  className="w-full justify-center text-base bg-blue-600 text-white hover:bg-blue-700 border-0"
                  aria-label="Request a demo"
                >
                  <span>Book a demo</span>
                </Button>
              </div>
            </div>

            {/* Close Button */}
            <button 
              className="absolute top-4 right-6 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close navigation menu"
            >
              <X className="w-6 h-6 text-gray-500" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
        </div>
    </header>

      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </NavbarErrorBoundary>
  );
};

export default Navbar;
